import { memo, ReactElement, useCallback, ChangeEvent } from 'react';
import { Input, Button, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import type { JobNumberItem } from '../types';
import { JobNumberRowGrid, RowActions } from './styles';

interface Props {
  item: JobNumberItem;
  canCommit: boolean;
  onChangeJobNumber: (id: string, value: string) => void;
  onCommit: (id: string) => void;
  onRemove: (id: string) => void;
}

const JobNumberRow = memo(function JobNumberRow({
  item,
  canCommit,
  onChangeJobNumber,
  onCommit,
  onRemove,
}: Props): ReactElement {
  const { id, jobNumber, status } = item;
  const isEditing = status === 'editing';

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChangeJobNumber(id, e.target.value);
    },
    [id, onChangeJobNumber]
  );

  const handleCommit = useCallback(() => {
    onCommit(id);
  }, [id, onCommit]);

  const handleRemove = useCallback(() => {
    onRemove(id);
  }, [id, onRemove]);

  return (
    <JobNumberRowGrid>
      <Input
        value={jobNumber}
        placeholder='仕事Noを入力'
        onChange={handleChange}
        disabled={!isEditing}
        maxLength={20}
        aria-label='仕事No'
      />
      <RowActions>
        {isEditing ? (
          <>
            <Button
              size='small'
              type='primary'
              icon={<PlusOutlined />}
              onClick={handleCommit}
              disabled={!canCommit}
              aria-label='この行を追加'
            >
              追加
            </Button>
            <Button size='small' icon={<CloseOutlined />} onClick={handleRemove} aria-label='この行を取り消し'>
              取り消し
            </Button>
          </>
        ) : (
          <Popconfirm
            title='この行を削除しますか？'
            description={`仕事No: ${jobNumber}`}
            onConfirm={handleRemove}
            okText='削除'
            cancelText='キャンセル'
            okButtonProps={{ danger: true }}
          >
            <Button size='small' danger icon={<DeleteOutlined />} aria-label='この行を削除'>
              削除
            </Button>
          </Popconfirm>
        )}
      </RowActions>
    </JobNumberRowGrid>
  );
});

export default JobNumberRow;
